import { WarningAmber } from '@mui/icons-material';
import { 
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Typography
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchoolShift } from '../hooks/useSchoolShift';

interface Props {
  state: ReturnType<typeof useSchoolShift>['state'];
  actions: ReturnType<typeof useSchoolShift>['actions'];
}

export default function SchoolShiftDeleteModal({ state, actions }: Props) {
  const { deleteModalOpen, shiftToDelete, isDeleting } = state;
  const { setDeleteModalOpen, handleConfirmDelete } = actions;
  const { t } = useTranslation();
  
  const [expanded, setExpanded] = useState(false);

  const handleClose = () => {
    if (!isDeleting) {
      setDeleteModalOpen(false);
      setTimeout(() => setExpanded(false), 300); 
    }
  };

  const linkedClasses = shiftToDelete?.linkedClasses || [];
  const hasClasses = linkedClasses.length > 0;
  const displayedClasses = expanded ? linkedClasses : linkedClasses.slice(0, 3);
  const hiddenCount = linkedClasses.length - 3;

  return (
    <Dialog 
        open={deleteModalOpen} 
        onClose={handleClose}
        slotProps={{ 
            paper: { 
            sx: { 
              bgcolor: 'background.paper', 
              borderColor: 'divider', 
              borderWidth: 1, 
              borderStyle: 'solid', 
              borderRadius: 3, 
              width: '100%', 
              maxWidth: 450,
              m: 2
            } 
            } 
        }}
        >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 3 }}>
        <WarningAmber color="warning" />
        <Typography variant="h6" fontWeight="bold">{t('schoolShiftPage.deleteTitle')}</Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
          {t('schoolShiftPage.deleteQuestion', { name: shiftToDelete?.name })}
        </Typography>
        
        {hasClasses && (
          <Box sx={{ bgcolor: 'rgba(245, 158, 11, 0.06)', border: '1px solid', borderColor: 'warning.main', borderRadius: 2, p: 2, mt: 1 }}>
            <Typography variant="body2" color="warning.main" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t('schoolShiftPage.deleteImpactTitle')}
            </Typography>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mb: 1 }}>
              {t('schoolShiftPage.deleteImpactDescription', { count: shiftToDelete?.linkedMembersCount || 0 })}
            </Typography>

            <Box component="ul" sx={{ m: 0, pl: 2, typography: 'caption', color: 'text.secondary' }}>
              {displayedClasses.map((c) => (
                <li key={c.id}>
                  <strong>{c.name}</strong>
                </li>
              ))}
            </Box>

            {hiddenCount > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: '0.75rem', textTransform: 'none', color: 'warning.main' }}
              >
                {expanded ? t('schoolShiftPage.deleteHide') : t('schoolShiftPage.deleteShowMore', { count: hiddenCount })}
              </Button>
            )}
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>{t('schoolShiftPage.irreversible')}</Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} color="inherit" disabled={isDeleting}>{t('schoolShiftPage.cancel')}</Button>
        <Button 
          onClick={handleConfirmDelete} 
          variant="contained" 
          color="error" 
          autoFocus 
          disabled={isDeleting} 
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isDeleting ? t('schoolShiftPage.deleting') : t('schoolShiftPage.confirmDelete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}